import { ReactNode, useState } from "react";

import { POLICY_CREATION_PROMPT } from "components/AssistantPanel/prompts";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import FiltersSidebar from "components/Filters/Sidebar";
import FiltersSplit from "components/Filters/Split";
import {
  FilterItem,
  FiltersItemsOptionsMap,
  SavedFilterView,
  SortOption,
} from "components/Filters/types";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import PageInfoHeader from "ds/components/PageInfoHeader";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import FiltersMobileTriggerButton from "components/Filters/MobileTriggerButton";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DesktopView from "components/Mobile/DesktopView";

import PoliciesFeatureGateCallout from "../FeatureGateCallout";
import PoliciesListCreatePolicyButton from "../CreatePolicyButton";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  FILTERS_TYPE,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "../constants";

type PoliciesFiltersLayoutProps = {
  children: ReactNode;
  isPageEmpty: boolean;
  openCreateDrawer: () => void;
  canCreatePolicy: boolean;
  filters: FilterItem[];
  filtersMap: FiltersItemsOptionsMap;
  sortOptions: SortOption[];
  filtersLoading: boolean;
  refetchActiveSections: (fields: string[]) => void;
};

const PoliciesFiltersLayout = ({
  filters,
  filtersMap,
  sortOptions,
  filtersLoading,
  refetchActiveSections,
  children,
  isPageEmpty,
  openCreateDrawer,
  canCreatePolicy,
}: PoliciesFiltersLayoutProps) => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  return (
    <>
      <PageInfoHeader title="Account policies">
        {!isPageEmpty && (
          <ViewHeaderWrapper direction="row" align="center">
            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.POLICIES}
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
            <FiltersMobileTriggerButton />
          </ViewHeaderWrapper>
        )}
        <DesktopView>
          {canCreatePolicy && (
            <PoliciesListCreatePolicyButton
              onCreateManually={openCreateDrawer}
              prompt={POLICY_CREATION_PROMPT}
              entryPoint="policy_creation"
              analyticsPage={AnalyticsPagePolicy.PoliciesList}
            />
          )}
        </DesktopView>
      </PageInfoHeader>

      <PoliciesFeatureGateCallout />

      <Filters
        filters={filters}
        filtersItemsOptionsMap={filtersMap}
        filtersLoading={filtersLoading}
        sortOptions={sortOptions}
        initialSortOption={INITIAL_SORT_OPTION}
        initialSortDirection={INITIAL_SORT_DIRECTION}
        pollActiveSections={refetchActiveSections}
        filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
        filtersType={FILTERS_TYPE}
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
      >
        <FiltersSplit>
          <FiltersSidebar analyticsPage={AnalyticsPagePolicy.PoliciesList} />

          <FiltersContentWrapper>{children}</FiltersContentWrapper>
        </FiltersSplit>
      </Filters>
    </>
  );
};

export default PoliciesFiltersLayout;
